/**
 * One status → intent mapping shared across every list page that renders a
 * status column — invoices, properties, maintenance requests, leases,
 * offboarding, eviction notices. Their status vocabularies differ, but they
 * all resolve to the same four intents (settled/good, needs-attention,
 * blocked/bad, dormant), so one mapping serves all of them rather than each
 * page inventing its own. Consumed by `components/carbon/StatusTag.tsx`.
 *
 * Extend the word lists below when a new page's status vocabulary introduces
 * a term not already covered — never hardcode a one-off tag color at a call
 * site, or the next status added to that same enum silently falls through to
 * the neutral default.
 */
export type StatusIntent = 'positive' | 'attention' | 'negative' | 'dormant';

const POSITIVE = new Set([
  'PAID',
  'APPROVED',
  'ACTIVE',
  'COMPLETED',
  'RESOLVED',
  'SUCCESS',
  'OCCUPIED',
  'CLEARED',
]);

const NEGATIVE = new Set([
  'OVERDUE',
  'REJECTED',
  'URGENT',
  'FAILED',
  'VOID',
  'CANCELLED',
  'EVICTED',
  'EXPIRED',
]);

const ATTENTION = new Set([
  'PENDING_APPROVAL',
  'PENDING',
  'NEW',
  'PARTIAL',
  'IN_PROGRESS',
  'OPEN',
  'EXPIRING_SOON',
]);

const DORMANT = new Set(['DRAFT', 'INACTIVE', 'VACANT', 'CLOSED', 'NO_ACTIVE_LEASE']);

export function statusIntent(status: string): StatusIntent {
  if (POSITIVE.has(status)) return 'positive';
  if (NEGATIVE.has(status)) return 'negative';
  if (ATTENTION.has(status)) return 'attention';
  if (DORMANT.has(status)) return 'dormant';
  return 'attention';
}
